'use client';
import { useCallback, useEffect, useState } from 'react';
import { useT } from '../../lib/i18n';
import { Icon } from '../Icon';
import { Working } from '../Working';
import { toast } from '../Toast';
import { confirmLayer } from '../Alert';
import { BoardPager } from './BoardBits';
import { boardStamp, maskName, maskUid, type BoardList, type BoardRow } from './types';

/**
 * 댓글형 게시판 — 가이드 `event-board.html`.
 *
 * **목록이 곧 본문이다** — 제목도 상세 화면도 없다. 한 줄씩 쌓이고, 자기 글은 그 자리에서
 * 고치거나 지운다. 기본게시판과 다른 것은 **화면 구조뿐**이고 권한·알림 규칙은 같다
 * (2026-09-01 사용자 확정).
 *
 * 가이드 위쪽의 이벤트 안내 상자(`rvc-event-banner`)는 **그리지 않는다**(사용자 지시) —
 * 그 페이지의 예시이지 게시판의 일부가 아니다.
 *
 * 클라이언트다 — 쓰기·수정·삭제가 그 자리에서 일어나고 목록을 다시 받는다.
 */
const ICON_EDIT = 'pencil';
const ICON_DEL = 'trash-2';
const MAX_LEN = 400;

export function CommentBoard({ settingId, initial, loggedIn }: {
  settingId: number;
  /** 서버가 미리 받아 둔 첫 쪽 — 첫 화면이 비어 보이지 않게 */
  initial: BoardList;
  loggedIn: boolean;
}) {
  const t = useT();
  const [data, setData] = useState<BoardList>(initial);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (page: number) => {
    // 한 쪽 분량은 **서버가 게시판 설정으로 정한다**(2026-09-02) — 화면이 보내지 않는다
    const res = await fetch(`/api/boards/${settingId}?page=${page}`).catch(() => null);
    if (res?.ok) setData((await res.json()) as BoardList);
  }, [settingId]);

  // 주소의 쪽이 바뀌면 다시 받는다(페이저가 주소를 바꾼다)
  useEffect(() => { if (data.page !== initial.page) void load(initial.page); }, [initial.page, data.page, load]);

  /** 서버에 보내고 목록을 다시 받는다 — 실패 문구는 서버 것을 그대로 보여 준다 */
  const send = async (url: string, method: string, body?: unknown) => {
    setBusy(true);
    try {
      const res = await fetch(url, {
        method,
        ...(body ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {}),
      });
      const out = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) { toast(out.message ?? t('acc.err.network'), 'error'); return false; }
      await load(data.page);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    // 잠그지 않고 눌리게 둔다 — 왜 안 되는지 말해야 한다(2026-09-01 전역 규칙)
    if (!loggedIn) { toast(t('bbs.loginToWrite'), 'error'); return; }
    if (!text.trim()) { toast(t('bbs.needText'), 'error'); return; }
    /*
     * 한 줄 글은 **제목 칸에 담는다**(2026-09-01). 댓글형은 제목이 따로 없고, 목록 응답이
     * 주는 것은 제목뿐이다 — 본문에 담으면 줄마다 상세를 한 번 더 불러야 한다.
     * 화면에서는 글자로 그리므로(JSX 가 이스케이프한다) HTML 이 섞여도 그대로 보인다.
     */
    if (await send('/api/boards/posts', 'POST', { settingId, subject: text.trim(), content: '' })) setText('');
  };

  const saveEdit = async (id: number) => {
    if (!editText.trim()) { toast(t('bbs.needText'), 'error'); return; }
    if (await send(`/api/boards/posts/${id}`, 'PUT', { subject: editText.trim() })) setEditing(null);
  };

  const remove = async (id: number) => {
    if (!(await confirmLayer(t('bbs.deleteAsk'), { kind: 'danger', okLabel: t('common.delete') }))) return;
    await send(`/api/boards/posts/${id}`, 'DELETE');
  };

  const rows: BoardRow[] = [...data.notices, ...data.rows];

  return (
    <>
      {/*
        쓰기 상자는 **목록 위**다(2026-09-01 사용자 지시 — 가이드는 아래에 둔다).
        목록이 최신순이라 새 글이 바로 위에 쌓이고, 길어져도 쓰러 내려갈 일이 없다.
        목록과의 사이는 **20px**(2026-09-02 사용자 확정).
      */}
      {/* 로그인 전에도 그린다 — 누르면 로그인이 필요하다고 말한다 */}
      <div className="rvc-write mb20">
        <textarea className="rvc-textarea" maxLength={MAX_LEN} value={text}
          placeholder={t('bbs.commentPh')} onChange={(e) => setText(e.target.value)} />
        <div className="rvc-write-footer">
          <span className="rvc-count">{t('bbs.charCount', { n: text.length, max: MAX_LEN })}</span>
          <button className="action-btn action-submit btn-sm" disabled={busy} onClick={() => void submit()}>
            {busy ? <Working inline /> : null}{t('bbs.submit')}
          </button>
        </div>
      </div>

      {/* 가이드는 빈 상태를 목록 **안에** 두고 `.rvc-list.is-empty` 로 여닫는다 */}
      <div className={`rvc-list mb16${rows.length ? '' : ' is-empty'}`}>
        {/*
          가이드는 `.rvc-item.editing` **클래스로** 여닫는다(`.editing .rvc-text`·`.rvc-actions`
          를 감추고 `.editing .rvc-edit-box` 를 편다). 조건부로 그리면 그 CSS 가 헛돈다.
        */}
        {rows.map((r) => (
          <div className={`rvc-item${editing === r.id ? ' editing' : ''}`} key={r.id}>
            <div className="rvc-item-head">
              {/* 가이드 그대로 `이름(아이디)` — **이름도 가린다**(2026-09-01 지시) */}
              <span className="rvc-author">
                {maskName(r.writerName) || r.writer}
                <span className="rvc-author-mask">({maskUid(r.writer)})</span>
              </span>
              <span className="rvc-date">{boardStamp(r.createdAt)}</span>
            </div>
            <p className="rvc-text">{r.subject}</p>
            {/*
              **서버가 준 `canEdit` 을 쓴다**(2026-09-01 사용자 제보). 전에는 `writer === myUid`
              로 화면이 다시 셈해서 **관리자에게 남의 글 버튼이 안 나왔다** — 서버는 작성자와
              관리자 둘 다 고칠 수 있다고 준다. 화면이 권한을 스스로 셈하면 서버와 갈린다.
            */}
            {r.canEdit && (
              <>
                <div className="rvc-actions">
                  <button className="rvc-action-btn rvc-edit" type="button"
                    onClick={() => { setEditing(r.id); setEditText(r.subject ?? ''); }}>
                    <Icon name={ICON_EDIT} /> {t('action.edit')}
                  </button>
                  <button className="rvc-action-btn rvc-del" type="button" onClick={() => void remove(r.id)}>
                    <Icon name={ICON_DEL} /> {t('common.delete')}
                  </button>
                </div>
                <div className="rvc-edit-box">
                  <textarea className="rvc-edit-textarea" maxLength={MAX_LEN}
                    value={editing === r.id ? editText : (r.subject ?? '')}
                    onChange={(e) => setEditText(e.target.value)} />
                  <div className="rvc-edit-actions">
                    <button className="action-btn action-cancel btn-sm" onClick={() => setEditing(null)}>{t('action.cancel')}</button>
                    <button className="action-btn action-submit btn-sm" disabled={busy} onClick={() => void saveEdit(r.id)}>
                      {busy ? <Working inline /> : null}{t('action.save')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        <div className="rvc-empty">{t('bbs.commentEmpty')}</div>
      </div>

      <BoardPager page={data.page} total={data.total} size={data.size} />
    </>
  );
}
